"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Icon = _interopRequireWildcard(require("./Icon"));
var _StackView = require("../StackView");
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Returns an icon and some text with a sized gap between them. This is a utility component
 * intended for use when creating components, not intended for use in applications on its own.
 *
 * IconText does not wrap its children, so should be used either:
 *  - inline within a <Text> element
 *  - within a container with `flexDirection: 'row'`
 */const IconText = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    space,
    iconPosition = 'left',
    icon: IconComponent,
    iconProps,
    children
  } = _ref;
  const iconContent = IconComponent && /*#__PURE__*/(0, _jsxRuntime.jsx)(_Icon.default, {
    ref: ref,
    icon: IconComponent,
    scalesWithText: true,
    ...iconProps
  });

  // Inline images on Android are always baseline-aligned which makes them look misaligned - offset it.
  // See abandoned issue https://github.com/facebook/react-native/issues/6529
  const size = iconProps?.tokens?.size ?? 0;
  const valueTranslateY = iconProps?.tokens?.translateY;
  /**
   * These calculations were carried out using a set of linear equations to calculate that the
   * position of the icon "->"" is aligned to the first line of the tooltip text on IOS and Android.
   * The issue was mainly on IOS, the translateY style didn't match with the old calculations.
   */
  const resultY = valueTranslateY ? Math.floor(-1 * (valueTranslateY - 4)) : 0;
  const iconAdjustedAndroid = /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    style: {
      transform: [{
        translateY: valueTranslateY ? size * 0.2 : size * 0.01
      }]
    },
    children: iconContent
  });
  const iconAdjustedIOS = /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    style: {
      transform: [{
        translateY: valueTranslateY ? resultY : size * 0.01
      }]
    },
    children: iconContent
  });
  const mobile = _Platform.default.OS === 'android' ? iconAdjustedAndroid : iconAdjustedIOS;
  const adjustedContainer = _Platform.default.OS === 'web' ? iconContent : mobile;
  const adjustedContainerWithPosition = /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
    style: staticStyles.adjustedContainer,
    children: adjustedContainer
  });
  if (iconPosition === 'inline') {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Text.default, {
      children: [children, " ", adjustedContainerWithPosition]
    });
  }
  return (0, _StackView.getStackedContent)(iconPosition === 'left' ? [adjustedContainer, children] : [children, adjustedContainer], {
    space,
    direction: 'row'
  });
});
IconText.displayName = 'IconText';
IconText.propTypes = {
  /**
   * Amount of space to separate the text content and icon. Uses the themes's spacing scale
   * (see useSpacingScale for more info).
   */
  space: _utils.spacingProps.types.spacingValue,
  /**
   * Whether the icon should be to the left of the content, the right of the content or inline with the content.
   */
  iconPosition: _propTypes.default.oneOf(['left', 'right', 'inline']),
  /**
   * A valid UDS icon component imported from a UDS palette.
   */
  icon: _propTypes.default.elementType,
  /**
   * Props that will be passed to the icon component. By default the icon's `scalesWithText`
   * prop will be set as "true" so that the icon continues to match the size of the text
   * if users use accessibility settings to increase text size.
   */
  iconProps: _propTypes.default.exact(_Icon.iconComponentPropTypes),
  /**
   * Content to be rendered alongside the Icon component. Usually this should be a
   * `<Typography>` component, or a component that renders `<Text>`.
   */
  children: _propTypes.default.node
  /* eslint-enable react/no-unused-prop-types */
};
const staticStyles = _StyleSheet.default.create({
  adjustedContainer: {
    position: 'absolute'
  }
});
var _default = exports.default = IconText;